import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  api,
  APIError,
  type Course,
  type CourseSession,
  type GuideItemPayload,
  type TlaAttachment,
  type TrainerGuide,
  type TrainerGuideBreak,
  type TrainerGuideBreakRule,
  type TrainerGuideRow,
} from "@/api/client";
import { useDateLocale } from "@/lib/labels";

// Editierbarer Trainingsleitfaden (SessionLab-Look): Timeline-Karten mit
// Drag & Drop, Dauer-Editor, eigenen Blöcken und Pausen. Die Verplanung
// (Pausen-Kollision, Puffer, Plausibilität) rechnet das Backend — der Editor
// sendet den Block-Zustand debounced und bekommt die Zeiten zurück.
// Persistenz: Eingaben + Blöcke in course.metadata.trainer_guide.

interface EditableItem {
  uid: string;
  title: string;
  description: string | null;
  duration_minutes: number | null;
  kind: "method" | "custom";
  linked_ilos: number[];
  attachments: TlaAttachment[];
}

function newUid(): string {
  return crypto.randomUUID();
}

function toPayload(items: EditableItem[]): GuideItemPayload[] {
  return items.map(({ uid: _uid, ...rest }) => rest);
}

// Verplante Methoden-/Custom-Rows des Guides zurück in Editor-Blöcke wandeln.
function rowsToItems(rows: TrainerGuideRow[]): EditableItem[] {
  return rows
    .filter((r) => r.kind === "method" || r.kind === "custom")
    .map((r) => ({
      uid: newUid(),
      title: r.title,
      description: r.description ?? null,
      duration_minutes: r.duration_minutes,
      kind: r.kind === "custom" ? "custom" : "method",
      linked_ilos: r.linked_ilos,
      attachments: r.attachments ?? [],
    }));
}

// Minuten → "2 h 30 min" / "45 min" (Einheiten sind Symbole, sprachneutral)
function fmtDur(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h <= 0) return `${m} min`;
  return m > 0 ? `${h} h ${m} min` : `${h} h`;
}

export default function TrainerGuideCard({
  course,
  tlaLabel,
}: {
  course: Course;
  tlaLabel?: (id: string | undefined) => string;
}) {
  const { t, i18n } = useTranslation();
  const dateLocale = useDateLocale();
  const isEn = i18n.language.startsWith("en");
  // Fallback-Label, wenn der Host kein tlaLabel übergibt
  const labelFor = tlaLabel ?? ((id?: string) => id ?? t("guide.method"));

  // Minuten → gerundete Stunden ("~2,5 h" / "~2.5 h"), unter 60 min als "~45 min"
  const fmtHoursApprox = (mins: number): string => {
    if (mins < 60) return `~${mins} min`;
    const hours = (mins / 60).toFixed(1);
    return `~${isEn ? hours : hours.replace(".", ",")} h`;
  };

  const saved = (course.metadata?.trainer_guide ?? {}) as {
    date?: string;
    start?: string;
    end?: string;
    breaks?: TrainerGuideBreak[];
    items?: GuideItemPayload[];
    buffer_pct?: number;
    break_rule?: TrainerGuideBreakRule | null;
    session_id?: string | null;
  };
  const [date, setDate] = useState(saved.date ?? new Date().toISOString().slice(0, 10));
  const [start, setStart] = useState(saved.start ?? "09:00");
  const [end, setEnd] = useState(saved.end ?? "17:00");
  const [breaks, setBreaks] = useState<TrainerGuideBreak[]>(saved.breaks ?? []);
  const [bufferPct, setBufferPct] = useState(saved.buffer_pct ?? 10);
  const [breakRuleOn, setBreakRuleOn] = useState(!!saved.break_rule);
  const [breakEvery, setBreakEvery] = useState(saved.break_rule?.every_minutes ?? 90);
  const [breakLen, setBreakLen] = useState(saved.break_rule?.break_minutes ?? 15);
  const [sessionId, setSessionId] = useState<string | null>(saved.session_id ?? null);
  const [sessions, setSessions] = useState<CourseSession[]>([]);
  const [items, setItems] = useState<EditableItem[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [guide, setGuide] = useState<TrainerGuide | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addTlaId, setAddTlaId] = useState("");
  const dragIndex = useRef<number | null>(null);

  const hasMethods = (course.tlas ?? []).length > 0;

  // Aktive Pausenregel als Request-Wert (aus = null).
  function currentBreakRule(): TrainerGuideBreakRule | null {
    return breakRuleOn && breakEvery > 0 && breakLen > 0
      ? { every_minutes: breakEvery, break_minutes: breakLen }
      : null;
  }

  // Sessions des Kurses für den Session-Wähler laden (optional — bei Fehler
  // bleibt der Wähler einfach verborgen).
  useEffect(() => {
    api.listSessions(course.id).then(setSessions).catch(() => {});
  }, [course.id]);

  // Initialzustand: gespeicherte Blöcke übernehmen ODER einmal ohne items
  // berechnen lassen — das Backend leitet dann die Blöcke aus den Kurs-TLAs
  // ab (mit deutschen Labels), die der Editor als Startzustand übernimmt.
  useEffect(() => {
    if (saved.items && saved.items.length > 0) {
      setItems(
        saved.items.map((it) => ({
          uid: newUid(),
          title: it.title,
          description: it.description ?? null,
          duration_minutes: it.duration_minutes ?? null,
          kind: it.kind ?? "method",
          linked_ilos: it.linked_ilos ?? [],
          attachments: it.attachments ?? [],
        })),
      );
      setInitialized(true);
      return;
    }
    if (!hasMethods || !date) return;
    (async () => {
      setBusy(true);
      try {
        const g = await api.trainerGuide(course.id, {
          date, start, end, breaks,
          buffer_pct: bufferPct,
          break_rule: currentBreakRule(),
          session_id: sessionId,
        });
        setGuide(g);
        setItems(rowsToItems(g.rows));
        setInitialized(true);
      } catch (e) {
        setError(e instanceof APIError ? e.detail : String(e));
      } finally {
        setBusy(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Jede Änderung (Blöcke, Zeiten, Pausen, Puffer, Regeln) → debounced neu
  // verplanen und den Eingabe-Zustand in course.metadata.trainer_guide sichern.
  const computeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!initialized || !date || !start || !end) return;
    if (computeTimer.current) clearTimeout(computeTimer.current);
    computeTimer.current = setTimeout(async () => {
      setBusy(true);
      setError(null);
      try {
        const payload = toPayload(items);
        const breakRule = currentBreakRule();
        const g = await api.trainerGuide(course.id, {
          date, start, end, breaks, items: payload,
          buffer_pct: bufferPct,
          break_rule: breakRule,
          session_id: sessionId,
        });
        setGuide(g);
        api.updateCourse(course.id, {
          metadata: {
            trainer_guide: {
              date, start, end, breaks, items: payload,
              buffer_pct: bufferPct,
              break_rule: breakRule,
              session_id: sessionId,
            },
          },
        }).catch(() => {});
      } catch (e) {
        setError(e instanceof APIError ? e.detail : String(e));
      } finally {
        setBusy(false);
      }
    }, 450);
    return () => {
      if (computeTimer.current) clearTimeout(computeTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, items, date, start, end, breaks, bufferPct, breakRuleOn, breakEvery, breakLen, sessionId]);

  // ── Block-Operationen ────────────────────────────────────────────────
  function patchItem(i: number, patch: Partial<EditableItem>) {
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  }
  function removeItem(i: number) {
    setItems((prev) => prev.filter((_, idx) => idx !== i));
  }
  function moveItem(from: number, to: number) {
    if (from === to) return;
    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }
  function addCustomBlock() {
    setItems((prev) => [
      ...prev,
      { uid: newUid(), title: t("guide.newBlock"), description: null, duration_minutes: 30, kind: "custom", linked_ilos: [], attachments: [] },
    ]);
  }
  function addCourseTla() {
    const tla = (course.tlas ?? []).find((x) => x.id === addTlaId);
    if (!tla) return;
    setItems((prev) => [
      ...prev,
      {
        uid: newUid(),
        title: labelFor(tla.type),
        description: tla.description ?? null,
        duration_minutes: tla.duration_minutes ?? null,
        kind: "method",
        linked_ilos: [],
        attachments: tla.extras?.attachments ?? [],
      },
    ]);
    setAddTlaId("");
  }
  function resetToCourseTlas() {
    (async () => {
      setBusy(true);
      try {
        const g = await api.trainerGuide(course.id, {
          date, start, end, breaks,
          buffer_pct: bufferPct,
          break_rule: currentBreakRule(),
          session_id: sessionId,
        });
        setGuide(g);
        setItems(rowsToItems(g.rows));
      } catch (e) {
        setError(e instanceof APIError ? e.detail : String(e));
      } finally {
        setBusy(false);
      }
    })();
  }

  // Session-Wahl: Datum/Zeiten aus der Session vorbefüllen (bleiben editierbar)
  // und die Blöcke für den neuen Scope (nur TLAs dieser Session bzw. alle)
  // frisch vom Backend ableiten. Persistenz läuft über den Debounce-Effekt.
  function handleSessionChange(value: string) {
    const sid = value || null;
    setSessionId(sid);
    const s = sessions.find((x) => x.id === sid);
    const nextDate = s?.date || date;
    const nextStart = s?.start || start;
    const nextEnd = s?.end || end;
    if (s?.date) setDate(s.date);
    if (s?.start) setStart(s.start);
    if (s?.end) setEnd(s.end);
    (async () => {
      setBusy(true);
      setError(null);
      try {
        const g = await api.trainerGuide(course.id, {
          date: nextDate, start: nextStart, end: nextEnd, breaks,
          buffer_pct: bufferPct,
          break_rule: currentBreakRule(),
          session_id: sid,
        });
        setGuide(g);
        setItems(rowsToItems(g.rows));
      } catch (e) {
        setError(e instanceof APIError ? e.detail : String(e));
      } finally {
        setBusy(false);
      }
    })();
  }

  function downloadCsv() {
    if (!guide) return;
    const esc = (s: string) => `"${(s ?? "").replace(/"/g, '""')}"`;
    const header = [
      t("guide.csv.date"), t("guide.csv.from"), t("guide.csv.to"),
      t("guide.csv.duration"), t("guide.csv.block"), t("guide.csv.content"),
      t("guide.csv.iloRef"), t("guide.csv.material"),
    ];
    const kindLabel = (k: string) =>
      k === "method" ? t("guide.kind.method") :
      k === "custom" ? t("guide.kind.custom") :
      k === "break" ? t("guide.kind.break") : t("guide.kind.buffer");
    const lines = guide.rows.map((r) => [
      guide.date, r.start, r.end, String(r.duration_minutes), kindLabel(r.kind),
      esc(r.title + (r.description ? ` — ${r.description}` : "")),
      r.linked_ilos.length ? r.linked_ilos.map((n) => `LZ ${n}`).join(", ") : "",
      esc((r.attachments ?? []).map((a) => a.title).join(" · ")),
    ]);
    const csv = "\uFEFF" + [header, ...lines].map((l) => l.join(";")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    const slug = (course.title || "seminar").replace(/[^a-zA-Z0-9]/g, "_").slice(0, 40);
    a.href = url;
    a.download = `${slug}_trainingsleitfaden_${guide.date}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  if (!hasMethods) {
    return (
      <section className="mt-12 p-6 bg-ca-card rounded-2xl border border-ca-surface-border">
        <h3 className="text-xs font-black uppercase tracking-widest text-ca-surface-muted mb-1">
          {t("guide.title")}
        </h3>
        <p className="text-sm text-ca-surface-muted italic">
          {t("guide.noMethods")}
        </p>
      </section>
    );
  }

  let editableIdx = 0;

  // Plausibilitäts-Ampel auf Basis von realistic_load_pct (Methoden + Puffer
  // vs. verfügbare Zeit): ≤ 95 realistisch, 96–105 sehr eng, > 105 überladen.
  const loadPct = guide?.realistic_load_pct ?? 0;
  const loadStatus = !guide ? "ok" : loadPct <= 95 ? "ok" : loadPct <= 105 ? "tight" : "over";
  const loadClass =
    loadStatus === "ok" ? "bg-verdict-green-bg text-verdict-green" :
    loadStatus === "tight" ? "bg-verdict-amber-bg text-verdict-amber" :
    "bg-verdict-red-bg text-verdict-red";
  const loadText =
    loadStatus === "ok" ? t("guide.loadOk") :
    loadStatus === "tight" ? t("guide.loadTight") :
    t("guide.loadOver", { mins: guide?.overflow_minutes ?? 0 });
  const methodBlocks = guide
    ? guide.rows.filter((r) => r.kind === "method" || r.kind === "custom").length
    : 0;

  // Transfer-Aktivitäten (Phase "between") — Lernaufwand zwischen den Terminen.
  const betweenTlas = (course.tlas ?? []).filter((x) => x.phase === "between");
  const transferMinutes = betweenTlas.reduce((sum, x) => sum + (x.duration_minutes ?? 45), 0);

  return (
    <section className="mt-12 p-6 bg-ca-card rounded-2xl border border-ca-surface-border">
      <div className="flex items-center gap-3 mb-1">
        <h3 className="text-xs font-black uppercase tracking-widest text-ca-surface-muted">
          {t("guide.title")}
        </h3>
        {busy && <span className="text-[10px] text-ca-surface-muted">{t("guide.computing")}</span>}
        <div className="flex-1" />
        <button
          onClick={resetToCourseTlas}
          title={t("guide.resetTitle")}
          className="text-[10px] font-semibold text-ca-surface-muted hover:text-ca-surface-accent transition"
        >
          {t("guide.reset")}
        </button>
      </div>
      <p className="text-sm text-ca-surface-muted mb-5">
        {t("guide.subtitle")}
      </p>

      {/* Session-Wahl (nur bei Mehr-Termin-Formaten) */}
      {sessions.length > 0 && (
        <div className="mb-4">
          <label className="text-xs font-bold text-ca-surface-muted">
            {t("guide.session")}
            <select
              value={sessionId ?? ""}
              onChange={(e) => handleSessionChange(e.target.value)}
              className="block mt-1 bg-white border border-ca-surface-border rounded-md px-3 py-2 text-ca-surface-ink focus:border-ca-accent"
            >
              <option value="">{t("guide.wholeCourse")}</option>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title}{s.date ? ` · ${new Date(`${s.date}T00:00:00`).toLocaleDateString(dateLocale)}` : ""}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {/* Tagesrahmen */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="text-xs font-bold text-ca-surface-muted">
          {t("guide.day")}
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="block mt-1 bg-white border border-ca-surface-border rounded-md px-3 py-2 text-ca-surface-ink focus:border-ca-accent" />
        </label>
        <label className="text-xs font-bold text-ca-surface-muted">
          {t("guide.start")}
          <input type="time" value={start} onChange={(e) => setStart(e.target.value)}
            className="block mt-1 bg-white border border-ca-surface-border rounded-md px-3 py-2 text-ca-surface-ink focus:border-ca-accent" />
        </label>
        <label className="text-xs font-bold text-ca-surface-muted">
          {t("guide.end")}
          <input type="time" value={end} onChange={(e) => setEnd(e.target.value)}
            className="block mt-1 bg-white border border-ca-surface-border rounded-md px-3 py-2 text-ca-surface-ink focus:border-ca-accent" />
        </label>
        <button
          type="button"
          onClick={() => setBreaks([...breaks, { start: "12:30", end: "13:15" }])}
          className="px-3 py-2 rounded-md border border-ca-surface-border bg-ca-card text-xs font-bold text-ca-surface-accent hover:border-ca-accent transition"
        >
          {t("guide.addBreak")}
        </button>
      </div>

      {/* Puffer + automatische Pausenregel */}
      <div className="flex flex-wrap items-end gap-x-8 gap-y-3 mb-4">
        <div className="text-xs font-bold text-ca-surface-muted">
          <span>
            {t("guide.load", { pct: bufferPct })}{" "}
            <span className="font-normal">{t("guide.bufferApprox", { mins: guide ? `${guide.buffer_minutes} min` : "–" })}</span>
          </span>
          <input
            type="range"
            min={0}
            max={30}
            step={5}
            value={bufferPct}
            onChange={(e) => setBufferPct(Number(e.target.value))}
            className="block mt-1 w-40 accent-ca-accent"
            title={t("guide.bufferTitle")}
          />
        </div>
        <div className="text-xs font-bold text-ca-surface-muted">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={breakRuleOn}
              onChange={(e) => setBreakRuleOn(e.target.checked)}
              className="accent-ca-accent"
            />
            {t("guide.breakRule")}
          </label>
          {breakRuleOn && (
            <div className="flex items-center gap-1 mt-1 font-normal text-ca-surface-ink">
              {t("guide.breakEvery")}
              <input
                type="number"
                min={15}
                step={5}
                value={breakEvery}
                onChange={(e) => setBreakEvery(Number(e.target.value) || 0)}
                className="w-14 text-xs text-center text-ca-surface-ink bg-white border border-ca-surface-border rounded px-1 py-0.5"
              />
              {t("guide.breakArrow")}
              <input
                type="number"
                min={5}
                step={5}
                value={breakLen}
                onChange={(e) => setBreakLen(Number(e.target.value) || 0)}
                className="w-14 text-xs text-center text-ca-surface-ink bg-white border border-ca-surface-border rounded px-1 py-0.5"
              />
              {t("guide.breakMinutes")}
            </div>
          )}
        </div>
      </div>

      {breaks.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {breaks.map((b, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-xs text-ca-surface-ink bg-ca-card border border-ca-surface-border rounded-md px-2 py-1">
              {t("guide.breakLabel")}
              <input type="time" value={b.start} onChange={(e) => setBreaks(breaks.map((x, idx) => (idx === i ? { ...x, start: e.target.value } : x)))}
                className="border border-ca-surface-border rounded px-1 text-ca-surface-ink bg-white" />
              –
              <input type="time" value={b.end} onChange={(e) => setBreaks(breaks.map((x, idx) => (idx === i ? { ...x, end: e.target.value } : x)))}
                className="border border-ca-surface-border rounded px-1 text-ca-surface-ink bg-white" />
              <button type="button" onClick={() => setBreaks(breaks.filter((_, idx) => idx !== i))}
                className="text-ca-surface-muted hover:text-verdict-red font-bold">✕</button>
            </span>
          ))}
        </div>
      )}

      {/* Plausibilität */}
      {guide && (
        <div className={`p-3 rounded-md mb-4 text-xs font-semibold ${loadClass}`}>
          {t("guide.methodBlocks", { count: methodBlocks })} · Σ {fmtDur(guide.method_minutes)}
          {guide.buffer_minutes > 0 && ` ${t("guide.plusBuffer", { dur: fmtDur(guide.buffer_minutes) })}`}
          {` ${t("guide.inAvailable", { dur: fmtDur(guide.available_minutes) })} — ${loadText}`}
          {guide.auto_breaks > 0 &&
            ` · ${t("guide.autoBreaks", { count: guide.auto_breaks })}`}
          {guide.overflow_minutes > 0 &&
            ` — ${t("guide.overflowHint")}`}
          {guide.missing_durations > 0 &&
            ` · ${t("guide.missingDurations", { count: guide.missing_durations })}`}
        </div>
      )}

      {error && <p className="text-xs text-verdict-red mb-3">{error}</p>}

      {/* Timeline (SessionLab-Look) */}
      {guide && (
        <div className="space-y-1.5 mb-4">
          {guide.rows.map((r, rowKey) => {
            const isEditable = r.kind === "method" || r.kind === "custom";
            const itemIdx = isEditable ? editableIdx++ : -1;
            const item = isEditable && itemIdx < items.length ? items[itemIdx] : undefined;
            const barColor =
              r.kind === "method" ? "border-l-ca-accent" :
              r.kind === "custom" ? "border-l-ca-surface-muted" : "border-l-ca-surface-border";
            return (
              <div
                key={rowKey}
                draggable={!!item}
                onDragStart={() => { if (item) dragIndex.current = itemIdx; }}
                onDragOver={(e) => { if (item) e.preventDefault(); }}
                onDrop={() => { if (item && dragIndex.current !== null) moveItem(dragIndex.current, itemIdx); dragIndex.current = null; }}
                className={`flex items-stretch gap-0 bg-ca-card rounded-lg border border-ca-surface-border border-l-4 ${barColor} overflow-hidden ${
                  item ? "cursor-grab active:cursor-grabbing" : ""
                } ${r.kind === "break" ? "bg-ca-card" : ""}`}
              >
                <div className="w-24 shrink-0 px-3 py-2.5 text-sm font-bold text-ca-surface-ink border-r border-ca-surface-border whitespace-nowrap">
                  {r.start}<span className="text-ca-surface-muted font-normal">–{r.end}</span>
                </div>
                <div className="flex-1 px-3 py-2 min-w-0">
                  {item && r.kind === "custom" ? (
                    <input
                      value={item.title}
                      onChange={(e) => patchItem(itemIdx, { title: e.target.value })}
                      className="font-semibold text-sm text-ca-surface-ink bg-transparent outline-none border-b border-transparent focus:border-ca-accent w-full"
                    />
                  ) : (
                    <span className={`font-semibold text-sm ${r.kind === "buffer" ? "italic text-ca-surface-muted" : "text-ca-surface-ink"}`}>
                      {r.title}
                    </span>
                  )}
                  {r.auto && (
                    <span
                      className="ml-2 text-[9px] font-bold uppercase tracking-wide text-ca-surface-muted bg-white border border-ca-surface-border rounded px-1 py-0.5 align-middle"
                      title={t("guide.autoTitle")}
                    >
                      auto
                    </span>
                  )}
                  {r.description && (
                    <p className="text-xs text-ca-surface-muted truncate mt-0.5">{r.description}</p>
                  )}
                  {r.linked_ilos.length > 0 && (
                    <span className="text-[10px] font-bold text-ca-surface-accent">
                      {r.linked_ilos.map((n) => `LZ ${n}`).join(" · ")}
                    </span>
                  )}
                  {(r.attachments ?? []).length > 0 && (
                    <div className="mt-1 space-y-1">
                      {(r.attachments ?? []).map((a, ai) => (
                        <details key={ai} className="rounded-md bg-white border border-ca-surface-border px-2 py-1">
                          <summary className="cursor-pointer text-xs font-semibold text-ca-surface-accent">
                            📎 {a.title}
                          </summary>
                          <div className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-ca-surface-ink">
                            {a.content.replace(/\*\*/g, "").replace(/^#{1,3}\s?/gm, "").replace(/^---$/gm, "")}
                          </div>
                        </details>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 px-2 shrink-0">
                  {item ? (
                    <>
                      <input
                        type="number"
                        min={5}
                        step={5}
                        value={item.duration_minutes ?? 45}
                        onChange={(e) => patchItem(itemIdx, { duration_minutes: Number(e.target.value) || null })}
                        className="w-14 text-xs text-center text-ca-surface-ink bg-white border border-ca-surface-border rounded px-1 py-0.5"
                        title={t("guide.durationTitle")}
                      />
                      <span className="text-[10px] text-ca-surface-muted">min</span>
                      <button onClick={() => moveItem(itemIdx, Math.max(0, itemIdx - 1))}
                        className="px-1 text-ca-surface-muted hover:text-ca-surface-accent text-sm font-bold" title={t("guide.moveUp")}>↑</button>
                      <button onClick={() => moveItem(itemIdx, Math.min(items.length - 1, itemIdx + 1))}
                        className="px-1 text-ca-surface-muted hover:text-ca-surface-accent text-sm font-bold" title={t("guide.moveDown")}>↓</button>
                      <button onClick={() => removeItem(itemIdx)}
                        className="px-1 text-ca-surface-muted hover:text-verdict-red text-sm font-bold" title={t("common.remove")}>✕</button>
                    </>
                  ) : (
                    <span className="text-xs text-ca-surface-muted">{r.duration_minutes} min</span>
                  )}
                </div>
                {item && (
                  <div className="px-2 flex items-center text-ca-surface-muted select-none" title={t("guide.dragTitle")}>
                    ⠿
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Transfer-Hinweis: Lernaufwand zwischen den Terminen */}
      {betweenTlas.length > 0 && (
        <p className="text-xs text-ca-surface-muted mb-4">
          {t("guide.transferHint", {
            duration: fmtHoursApprox(transferMinutes),
            activities: t("guide.transferActivities", { count: betweenTlas.length }),
          })}
        </p>
      )}

      {/* Blöcke hinzufügen + Export */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={addTlaId}
          onChange={(e) => setAddTlaId(e.target.value)}
          className="text-xs bg-white border border-ca-surface-border rounded-md px-2 py-2 text-ca-surface-ink"
        >
          <option value="">{t("guide.addMethodPlaceholder")}</option>
          {(course.tlas ?? []).map((x) => (
            <option key={x.id} value={x.id}>{labelFor(x.type)}</option>
          ))}
        </select>
        <button onClick={addCourseTla} disabled={!addTlaId}
          className="px-3 py-2 rounded-md border border-ca-surface-border bg-ca-card text-xs font-bold text-ca-surface-ink hover:border-ca-accent disabled:opacity-40 transition">
          {t("guide.add")}
        </button>
        <button onClick={addCustomBlock}
          className="px-3 py-2 rounded-md border border-ca-surface-border bg-ca-card text-xs font-bold text-ca-surface-accent hover:border-ca-accent transition">
          {t("guide.addCustom")}
        </button>
        <div className="flex-1" />
        {guide && (
          <button onClick={downloadCsv}
            className="px-4 py-2 rounded-md border border-ca-surface-border bg-ca-card text-ca-surface-ink text-xs font-bold hover:border-ca-accent transition">
            {t("guide.csvExport")}
          </button>
        )}
      </div>
    </section>
  );
}
